import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { directFetchData } from "../services/api.services";

// Create context
const AppContext = createContext();

// Constants
const CACHE_KEY = "appContextData";
const CACHE_TTL = 1000 * 60 * 10; // 10 menit

// endpoint list
const endpoints = {
  landingpage: "landing-page",
  service: "service",
  brand: "brand",
  portofolio: "portfolio",
  contact: "contact",
  about: "pages/who-we-are",
  home: "pages/home",
  blog: "blog",
  product: "product",
  productType: "product-type",
  benefit: "benefit",
  serviceBanner: "service/banner",
  project: "project",
  storyline: "storyline",
  menu: "menu",
  social: "social",
  integrationImg: "integration-image",
};

// AppProvider component
export const AppProvider = ({ children }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchAll = async () => {
      try {
        // 1. Check cache
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;

          if (!isExpired) {
            setData(parsed.data);
            setIsFetched(true);
            setLoading(false);
            return;
          } else {
            console.log("🕒 Cache expired, fetching new data...");
          }
        }

        // 2. Fetch from API
        const results = await Promise.all(
          Object.entries(endpoints).map(async ([key, endpoint]) => {
            try {
              const response = await directFetchData(endpoint);

              if (key === "home" || key === "about" || key === "project") {
                return [key, response];
              }

              return [key, response.data];
            } catch (err) {
              console.warn(`⚠️ Gagal fetch ${endpoint}:`, err.message);
              return [key, null];
            }
          })
        );

        const resultObject = Object.fromEntries(results);
        setData(resultObject);
        setIsFetched(true);

        // 3. Store cache
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            timestamp: Date.now(),
            data: resultObject,
          })
        );

      } catch (err) {
        console.error("❌ Error fetching all API:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Value context
  const value = useMemo(
    () => ({ ...data, isFetched, loading }),
    [data, isFetched, loading]
  );

  return (
    <AppContext.Provider value={value}>{children}</AppContext.Provider>
  );
};

// Custom hook
export const useAppContext = () => useContext(AppContext);
